import { motion } from "framer-motion";
import MapPin from "lucide-react/dist/esm/icons/map-pin";
import Phone from "lucide-react/dist/esm/icons/phone";
import Award from "lucide-react/dist/esm/icons/award";
import Star from "lucide-react/dist/esm/icons/star";
import Waves from "lucide-react/dist/esm/icons/waves";
import Home from "lucide-react/dist/esm/icons/home";
import Sun from "lucide-react/dist/esm/icons/sun";
import Building from "lucide-react/dist/esm/icons/building";
import Wifi from "lucide-react/dist/esm/icons/wifi";
import Compass from "lucide-react/dist/esm/icons/compass";
import UtensilsCrossed from "lucide-react/dist/esm/icons/utensils-crossed";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import Users from "lucide-react/dist/esm/icons/users";
import Mail from "lucide-react/dist/esm/icons/mail";
import { Link } from "react-router-dom";
import Lottie from "lottie-react";
import Layout from "@/components/layout/Layout";
import HeroSection from "@/components/ui/HeroSection";
import SectionHeading from "@/components/ui/SectionHeading";
import ApartmentCard from "@/components/apartments/ApartmentCard";
import ParallaxBanner from "@/components/ui/ParallaxBanner";
import ParticleField from "@/components/ui/ParticleField";
import { useApartments } from "@/hooks/useApartments";
import ApartmentFilter, { parseSize } from "@/components/apartments/ApartmentFilter";
import { useState, useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import heroImg from "@/assets/hero-nordsee.jpg";
import hausStrandnixeImg from "@/assets/haus-strandnixe-hero.jpg";
import hausPanorama from "@/assets/haus-panorama-hero.jpg";
import InhaberCard from "@/components/InhaberCard";
import beachAnim from "@/assets/lottie/beach.json";
import homeAnim from "@/assets/lottie/home.json";
import peopleAnim from "@/assets/lottie/people.json";
import shakeAnim from "@/assets/lottie/shake.json";
import { useHomepageSection } from "@/hooks/useCmsData";
import Seo, { breadcrumbLd, faqLd } from "@/components/seo/Seo";

// FAQ content — MUST stay 1:1 identical to the visible <section id="faq">.
const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "Wie ist die An- und Abreisezeit in den Ferienwohnungen?",
    a: "Die Anreise ist ab 12:00 Uhr möglich. Die Abreise erfolgt bis spätestens 10:00 Uhr am Abreisetag.",
  },
  {
    q: "Wie erfolgt die Zahlung und wann wird die Kaution zurückgezahlt?",
    a: "Nach der Buchungsbestätigung wird eine Anzahlung von 20 % innerhalb von 14 Tagen fällig. Der Restbetrag ist spätestens 28 Tage vor Anreise zu leisten. Die Kaution wird nach der Abreise innerhalb von 7 Tagen zurückgezahlt.",
  },
  {
    q: "Sind Haustiere in den Ferienwohnungen erlaubt?",
    a: "Haustiere sind grundsätzlich nur nach vorheriger Absprache und ausdrücklicher Zustimmung erlaubt. In ausgewählten Ferienwohnungen sind Hunde herzlich willkommen — bitte fragen Sie uns vorab an.",
  },
  {
    q: "Warum sollte ich direkt bei Familie Luca buchen?",
    a: "Wenn Sie direkt bei uns buchen, entfallen zusätzliche Plattformgebühren. Sie erhalten persönliche Betreuung vor Ort, kurze Kommunikationswege und die Sicherheit, dass Familie Luca Ihre Ferienwohnung seit 1986 selbst vermittelt.",
  },
  {
    q: "Wie weit sind die Ferienwohnungen vom Strand entfernt?",
    a: "Haus Panorama in Cuxhaven-Duhnen liegt nur wenige Gehminuten vom Sandstrand entfernt. Haus Strandnixe in Cuxhaven-Döse befindet sich strandnah in der Nähe der Kugelbake, ebenfalls bequem zu Fuß erreichbar.",
  },
];

// Sichtbare Google-Bewertungen — bewusst NICHT als aggregateRating/review-JSON-LD
// hinterlegt (Selbstreferenz-Regel für Review-Snippets).
const GOOGLE_REVIEWS = [
  {
    author: "Jenny Bremer-Rutkowski",
    quote:
      "Wir haben uns in der Ferienwohnung sehr wohl gefühlt. Sehr saubere, gepflegte Wohnung, tolle Ausstattung und Familie Luca ist sehr freundlich und hilfsbereit. Gerne wieder!",
  },
  {
    author: "Imke Rubbel",
    quote:
      "Absolut empfehlenswert. Die Wohnung war sauber, gut ausgestattet und die Lage direkt in Duhnen könnte kaum besser sein. Persönliche Betreuung inklusive.",
  },
  {
    author: "A R",
    quote:
      "Sehr netter Kontakt, unkomplizierte Buchung und eine tolle Wohnung. Wir kommen gerne wieder an die Nordsee.",
  },
  {
    author: "Arne Doormann",
    quote:
      "Super Ferienwohnung, alles top gepflegt und die Familie Luca ist immer erreichbar. Wir haben unseren Urlaub in Cuxhaven sehr genossen.",
  },
];

const GOOGLE_PROFILE_URL =
  "https://www.google.com/search?q=Ferienwohnungen+Luca+Cuxhaven";

const highlights = [
  { lottie: beachAnim, title: "Strandnahe Ferienwohnungen", desc: "Nur wenige Gehminuten zum Sandstrand in Cuxhaven-Duhnen", sub: "Perfekt für Ihren Nordseeurlaub direkt am Meer" },
  { lottie: homeAnim, title: "Ferienwohnungen in Cuxhaven-Duhnen", desc: "14 moderne Unterkünfte in bester Lage", sub: "Ideal für Paare, Familien und Erholungssuchende" },
  { lottie: peopleAnim, title: "Perfekt für Ihren Nordseeurlaub", desc: "Ruhig gelegen und zentral zugleich", sub: "Erleben Sie Erholung, Natur und kurze Wege zu allem" },
  { lottie: shakeAnim, title: "Persönlich & zuverlässig betreut", desc: "Ihre Unterkunft mit Service vor Ort", sub: "Direkter Ansprechpartner für einen sorgenfreien Aufenthalt" },
];

const ApartmentSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="rounded-2xl overflow-hidden border border-border/50">
        <Skeleton className="h-52 w-full" />
        <div className="p-5 space-y-3">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    ))}
  </div>
);

const Index = () => {
  const { data: panoramaApts, isLoading: loadingPanorama } = useApartments("panorama");
  const { data: strandnixeApts, isLoading: loadingStrandnixe } = useApartments("strandnixe");
  const heroSection = useHomepageSection("hero");

  const [filterGuests, setFilterGuests] = useState<number | null>(null);
  const [filterSize, setFilterSize] = useState<number | null>(null);
  const [filterFeatures, setFilterFeatures] = useState<string[]>([]);

  const filteredPanorama = useMemo(() => {
    if (!panoramaApts) return [];
    return panoramaApts.filter((a) => {
      const guestsOk = filterGuests === null || a.maxGuests === filterGuests;
      const sizeOk = filterSize === null || parseSize(a.size) === filterSize;
      const featuresOk =
        filterFeatures.length === 0 ||
        filterFeatures.every((f) => a.features?.includes(f));
      return guestsOk && sizeOk && featuresOk;
    });
  }, [panoramaApts, filterGuests, filterSize, filterFeatures]);


  const heroTitle = heroSection?.title?.trim() || "Ferienwohnungen in Cuxhaven-Duhnen an der Nordsee";
  const heroSubtitle = heroSection?.subtitle?.trim() || "Strandnahe Ferienwohnungen in Cuxhaven mieten – persönlich betreut seit 1986. Ideal für Paare, Familien und erholsamen Nordseeurlaub.";

  return (
    <Layout>
      <Seo
        title="Ferienwohnungen Cuxhaven-Duhnen & Döse | Strandnah seit 1986 | Luca"
        description="Strandnahe Ferienwohnungen in Cuxhaven-Duhnen und Döse direkt an der Nordsee. Familiäre Vermittlung seit 1986. Jetzt persönlich anfragen!"
        path="/"
        jsonLd={[
          breadcrumbLd([{ name: "Start", path: "/" }]),
          faqLd(FAQ_ITEMS.map((it) => ({ q: it.q, a: it.a }))),
        ]}
      />
      {/* Hero — Bild + H1 rendern statisch, ohne Motion-Delay (LCP). */}
      <HeroSection
        image="/hero-nordsee-1280.webp"
        avifSrcSet="/hero-nordsee-640.avif 640w, /hero-nordsee-960.avif 960w, /hero-nordsee-1280.avif 1280w, /hero-nordsee-1920.avif 1920w"
        webpSrcSet="/hero-nordsee-640.webp 640w, /hero-nordsee-960.webp 960w, /hero-nordsee-1280.webp 1280w, /hero-nordsee-1920.webp 1920w"
        sizes="100vw"
        videoSrc="/videos/hero-nordsee-1280.mp4"
        priority
        title={heroTitle}
        subtitle={heroSubtitle}
      >
        <div className="flex items-center justify-center gap-2 text-primary-foreground/95 text-sm font-body tracking-widest uppercase -mt-6 mb-2">
          <MapPin className="w-3.5 h-3.5" />
          <span>Cuxhaven-Duhnen • Nordsee</span>
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-6 sm:gap-8">
          {[
            { icon: Star, label: "Seit 1986" },
            { icon: Waves, label: "Strandnah" },
            { icon: Users, label: "Familiengeführt" },
            { icon: MapPin, label: "Top Lage in Duhnen" },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-2 text-primary-foreground text-sm sm:text-base font-body"
            >
              <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
              <span>{item.label}</span>
            </div>
          ))}
        </div>

        <p className="mt-4 text-primary-foreground/90 text-sm font-body">
          Direkt buchen – ohne Plattformgebühren
        </p>
      </HeroSection>

      {/* Welcome */}
      <section className="section-padding">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <SectionHeading
            title="Ferienwohnungen in Cuxhaven-Duhnen mieten – persönlich betreut von Familie Luca"
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Inhaber-Card links — vertikal zentriert relativ zum Fließtext rechts */}
            <div className="lg:col-span-5 lg:self-stretch flex items-center justify-center">
              <InhaberCard />
            </div>

            {/* Fließtext rechts */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 font-body text-base sm:text-lg text-muted-foreground leading-relaxed space-y-4"
            >
              <p>
                Sie suchen eine Ferienwohnung in Cuxhaven-Duhnen, die strandnah liegt, zentral gelegen ist und Ihnen echten Nordseeurlaub bietet? Dann sind Sie bei Familie Luca genau richtig. Seit 1986 vermitteln wir hochwertige Ferienwohnungen in Cuxhaven – persönlich, zuverlässig und mit echter Leidenschaft für unsere Gäste.
              </p>
              <p>
                Ob Ferienwohnung an der Nordsee mit Strandnähe, zentrale Unterkunft in Duhnen oder ruhige Lage für entspannte Tage: Bei uns finden Sie genau die passende Ferienwohnung in Cuxhaven, abgestimmt auf Ihre Wünsche. Unsere Unterkünfte eignen sich ideal für Paare, Familien oder einen erholsamen Urlaub am Meer.
              </p>
              <p>
                Seit über 35 Jahren stehen wir für Erfahrung, Vertrauen und persönliche Betreuung. Als familiengeführtes Unternehmen in zweiter Generation legen wir besonderen Wert darauf, dass Ihr Aufenthalt reibungslos und entspannt verläuft – von der Auswahl der Ferienwohnung bis zur Betreuung vor Ort.
              </p>
              <p>
                Profitieren Sie von kurzen Wegen zum Strand, zur Promenade und zu den schönsten Orten rund um die Nordsee. Wir helfen Ihnen dabei, die perfekte Ferienwohnung in Cuxhaven-Duhnen zu finden und direkt zu buchen – unkompliziert, persönlich und ohne Umwege.
              </p>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Highlights Strip — full-width immersive */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20 lg:pb-24 -mt-8 sm:-mt-12">
        <div className="container-narrow mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {highlights.map((h, i) => (
              <motion.div
                key={h.title}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12, ease: "easeOut" }}
                className="text-center group"
              >
                <div className="w-24 h-24 mx-auto mb-5 group-hover:scale-110 transition-all duration-300">
                  <Lottie animationData={h.lottie} loop autoplay className="w-24 h-24" />
                </div>
                <h3 className="font-display text-lg sm:text-xl font-bold text-foreground">{h.title}</h3>
                <p className="mt-2 font-body text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
                <p className="mt-1 font-body text-xs text-muted-foreground/70 leading-relaxed">{h.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOUSES SECTION ===== */}
      <section id="haeuser" className="scroll-mt-20">

        {/* --- HAUS PANORAMA DUHNEN --- */}
        <div id="haus-panorama" className="relative scroll-mt-20">
          <HeroSection
            image={hausPanorama}
            title="Ferienwohnungen in Cuxhaven-Duhnen – Haus Panorama"
            subtitle="Strandnahe Ferienwohnungen an der Nordsee – nur 3 Minuten zum Strand. Persönlich betreut seit 1986."
            height="h-[65vh] sm:h-[80vh] lg:h-[90vh]"
          >
            {/* Topline */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center justify-center gap-2 text-primary-foreground/70 text-sm font-body tracking-widest uppercase -mt-6 mb-2"
            >
              <MapPin className="w-3.5 h-3.5" />
              <span>Cuxhaven-Duhnen • Nordsee</span>
            </motion.div>

            {/* CTA Buttons removed per request */}

            {/* Trust Bar */}
            <div className="mt-6 flex flex-wrap justify-center gap-6 sm:gap-8">
              {[
                { icon: Home, label: "14 Ferienwohnungen" },
                { icon: Waves, label: "3 Min. zum Strand" },
                { icon: Star, label: "Familiengeführt seit 1986" },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                  className="flex items-center gap-2 text-primary-foreground/90 text-sm sm:text-base font-body"
                >
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                  <span>{item.label}</span>
                </motion.div>
              ))}
            </div>

            {/* USP */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-4 text-primary-foreground/60 text-sm font-body"
            >
              Direkt buchen – ohne Plattformgebühren
            </motion.p>
          </HeroSection>

          <div className="section-padding">
            <div className="container-narrow mx-auto max-w-[95vw] xl:max-w-[90vw]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-5xl mx-auto text-center font-body text-base sm:text-lg text-muted-foreground leading-relaxed mb-14 space-y-4"
              >
                <p>
                  Erleben Sie erholsamen Nordseeurlaub in einer strandnahen Ferienwohnung in Cuxhaven-Duhnen. Das Haus Panorama gehört zu den gefragtesten Unterkünften im Nordseeheilbad Cuxhaven und bietet Ihnen 14 individuell eingerichtete Ferienwohnungen in Cuxhaven-Duhnen, ideal für Paare, Familien oder einen entspannten Urlaub am Meer.
                </p>
                <p>
                  Genießen Sie kurze Wege von Ihrer Ferienwohnung zum Strand in Cuxhaven-Duhnen: Der Sandstrand, die Promenade sowie zahlreiche Restaurants, Cafés und Einkaufsmöglichkeiten im Zentrum von Duhnen sind bequem zu Fuß erreichbar. Die zentrale Lage macht Haus Panorama zur perfekten Unterkunft für Ihren Nordseeurlaub in Cuxhaven.
                </p>
                <p>
                  Ob Sie eine Ferienwohnung an der Nordsee strandnah mieten möchten, lange Spaziergänge entlang der Küste planen oder das einzigartige Wattenmeer entdecken wollen – hier finden Sie den idealen Ausgangspunkt. Die Kombination aus Komfort, Lage und maritimem Flair macht Haus Panorama zu einer der besten Adressen für eine Ferienwohnung in Cuxhaven-Duhnen.
                </p>
                <p>
                  Sichern Sie sich jetzt Ihre Ferienwohnung in Cuxhaven an der Nordsee und genießen Sie unvergessliche Tage direkt am Meer.
                </p>
              </motion.div>

              {!loadingPanorama && panoramaApts && panoramaApts.length > 0 && (
                <ApartmentFilter
                  apartments={panoramaApts}
                  selectedGuests={filterGuests}
                  selectedSize={filterSize}
                  selectedFeatures={filterFeatures}
                  onGuestsChange={setFilterGuests}
                  onSizeChange={setFilterSize}
                  onFeaturesChange={setFilterFeatures}
                />

              )}

              {loadingPanorama ? (
                <div className="flex flex-wrap justify-center gap-6">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)] lg:w-[calc(25%-1.125rem)] xl:w-[calc(20%-1.2rem)]">
                      <div className="rounded-2xl overflow-hidden border border-border/50">
                        <Skeleton className="h-52 w-full" />
                        <div className="p-5 space-y-3">
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-4 w-full" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredPanorama.length === 0 ? (
                <div className="text-center py-12 font-body text-muted-foreground">
                  Keine Ferienwohnungen entsprechen Ihrer Auswahl.
                </div>
              ) : (
                <div className="flex flex-wrap justify-center gap-6">
                  {filteredPanorama.map((apt, i) => (
                    <div key={apt.id} className="w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)] lg:w-[calc(25%-1.125rem)] xl:w-[calc(20%-1.2rem)]">
                      <ApartmentCard apartment={apt} index={i} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* --- HAUS STRANDNIXE DÖSE --- */}
        <div id="haus-strandnixe" className="relative scroll-mt-20">
          <HeroSection
            image={hausStrandnixeImg}
            title="Ferienwohnungen in Cuxhaven-Döse – Haus Strandnixe"
            subtitle="Strandnahe Ferienwohnungen an der Nordsee – nur wenige Minuten zur Kugelbake und zum Sandstrand. Ideal für erholsamen Urlaub in Cuxhaven."
            height="h-[65vh] sm:h-[80vh] lg:h-[90vh]"
          >
            {/* Topline */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center justify-center gap-2 text-primary-foreground/70 text-sm font-body tracking-widest uppercase -mt-6 mb-2"
            >
              <MapPin className="w-3.5 h-3.5" />
              <span>Cuxhaven-Döse • Nordsee</span>
            </motion.div>

            {/* CTA Buttons removed per request */}

            {/* Trust Bar */}
            <div className="mt-6 flex flex-wrap justify-center gap-6 sm:gap-8">
              {[
                { icon: Home, label: "Mehrere Ferienwohnungen" },
                { icon: Waves, label: "Strandnähe" },
                { icon: MapPin, label: "Top Lage an der Kugelbake" },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                  className="flex items-center gap-2 text-primary-foreground/90 text-sm sm:text-base font-body"
                >
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                  <span>{item.label}</span>
                </motion.div>
              ))}
            </div>

            {/* USP */}
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-4 text-primary-foreground/60 text-sm font-body"
            >
              Direkt buchen – ohne Plattformgebühren
            </motion.p>
          </HeroSection>

          <div className="section-padding">
            <div className="container-narrow mx-auto max-w-6xl">
              {/* Text with apartment card floated left, chips inline */}
              <div className="mx-auto">
                <div className="sm:float-left sm:mr-10 sm:mb-4 mb-8 w-full sm:w-80 md:w-96">
                  {loadingStrandnixe ? (
                    <ApartmentSkeleton />
                  ) : (
                    strandnixeApts?.map((apt, i) => (
                      <ApartmentCard key={apt.id} apartment={apt} index={i} />
                    ))
                  )}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="font-body text-base sm:text-lg text-muted-foreground leading-relaxed space-y-4 text-left"
                >
                  <p>
                    Verbringen Sie Ihren Urlaub in einer modernen Ferienwohnung im Haus Strandnixe in Cuxhaven-Döse – nur wenige Gehminuten vom Sandstrand, der Promenade und dem UNESCO-Weltnaturerbe Wattenmeer entfernt. Die strandnahe Lage in direkter Nähe zur Kugelbake macht diese Unterkunft zur idealen Wahl für einen entspannten Nordseeurlaub.
                  </p>
                  <p>
                    Die liebevoll eingerichtete Ferienwohnung verbindet den Komfort eines Hotels mit der Privatsphäre eines eigenen Zuhauses und bietet beste Voraussetzungen für erholsame Tage mit bis zu 4 Personen. Einkaufsmöglichkeiten, Restaurants und Cafés erreichen Sie bequem zu Fuß, ebenso wie zahlreiche Freizeitangebote entlang der Nordseeküste.
                  </p>
                  <p>
                    Ob Wattwanderungen, Spaziergänge am Meer, Fahrradtouren oder Ausflüge zur Insel Neuwerk – die Ferienwohnung in Cuxhaven-Döse ist der perfekte Ausgangspunkt, um die Nordsee in all ihren Facetten zu erleben.
                  </p>
                </motion.div>

                {/* Feature chips — flow inline next to the floated card */}
                <div className="flex flex-wrap gap-3 mt-6">
                  {[
                    { icon: MapPin, text: "Strandnähe" },
                    { icon: Compass, text: "Nähe zur Kugelbake" },
                    { icon: UtensilsCrossed, text: "Restaurants & Shops fußläufig" },
                    { icon: Waves, text: "Wattenmeer & Nordsee" },
                    { icon: Building, text: "Dialysepraxis im Haus" },
                    { icon: ShieldCheck, text: "Ruhige Lage" },
                  ].map((m, i) => (
                    <motion.div
                      key={m.text}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06, duration: 0.4 }}
                      className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2 hover:scale-[1.03] transition-transform duration-300"
                    >
                      <m.icon className="w-4 h-4 text-accent shrink-0" />
                      <span className="font-body text-sm text-foreground whitespace-nowrap">{m.text}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Clear float */}
                <div className="clear-both" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BEWERTUNGEN (sichtbar, kein JSON-LD wegen Selbstreferenz-Regel) ===== */}
      <section id="bewertungen" aria-labelledby="bewertungen-heading" className="section-padding scroll-mt-20 bg-background">
        <div className="container-narrow mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="text-center mb-10">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-muted-foreground">
              Was unsere Gäste sagen
            </span>
            <h2 id="bewertungen-heading" className="mt-3 font-display text-3xl sm:text-4xl font-bold text-foreground">
              Bewertungen unserer Gäste
            </h2>
            <div className="mt-5 flex flex-col items-center gap-2" aria-label="Google-Bewertung: 4,9 von 5 Sternen aus 12 Bewertungen">
              <div className="flex items-center gap-1" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-accent text-accent" />
                ))}
              </div>
              <p className="font-body text-base text-foreground">
                <strong className="font-semibold">4,9 von 5 Sternen</strong> aus 12 Google-Bewertungen
              </p>
              <a
                href={GOOGLE_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body text-sm text-accent hover:underline"
              >
                Alle Bewertungen bei Google ansehen
              </a>
            </div>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6" role="list">
            {GOOGLE_REVIEWS.map((r, i) => (
              <motion.li
                key={r.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass-card rounded-2xl p-6 flex flex-col gap-3"
              >
                <span role="img" aria-label="5 von 5 Sternen" className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-accent text-accent" aria-hidden="true" focusable="false" />
                  ))}
                </span>
                <blockquote className="font-body text-base text-foreground leading-relaxed">
                  „{r.quote}"
                </blockquote>
                <footer className="font-body text-sm text-muted-foreground flex items-center justify-between">
                  <cite className="not-italic font-medium text-foreground">{r.author}</cite>
                  <span className="text-xs">Quelle: Google</span>
                </footer>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== FAQ (1:1 mit FAQPage-JSON-LD) ===== */}
      <section id="faq" aria-labelledby="faq-heading" className="section-padding scroll-mt-20 bg-secondary/30">
        <div className="container-narrow mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-10">
            <span className="font-body text-sm tracking-[0.2em] uppercase text-muted-foreground">
              Häufige Fragen
            </span>
            <h2 id="faq-heading" className="mt-3 font-display text-3xl sm:text-4xl font-bold text-foreground">
              Antworten rund um Ihren Aufenthalt
            </h2>
          </div>

          <div className="space-y-4">
            {FAQ_ITEMS.map((item) => (
              <div
                key={item.q}
                className="rounded-xl border border-border/60 bg-background/80 backdrop-blur-sm"
              >
                <details className="group">
                  <summary className="cursor-pointer list-none px-5 sm:px-6 py-4 flex items-start justify-between gap-4 font-display text-base sm:text-lg font-semibold text-foreground">
                    <span className="inline">{item.q}</span>
                    <span
                      aria-hidden="true"
                      className="mt-1 shrink-0 text-accent transition-transform duration-200 group-open:rotate-45 text-2xl leading-none"
                    >
                      +
                    </span>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 -mt-1 font-body text-base text-foreground/85 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — SEO + Conversion optimized contact section */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-ocean-gradient" />
        <ParticleField count={20} color="255, 255, 255" />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-primary-foreground/5 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-accent/10 blur-3xl" />

        <div className="relative z-10 container-narrow mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-body text-sm tracking-[0.2em] uppercase text-primary-foreground/50 mb-6"
            >
              Jetzt unverbindlich anfragen
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground text-center leading-tight"
            >
              Ferienwohnung in Cuxhaven-Duhnen
              <br />
              <span className="text-accent">jetzt anfragen</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mt-6 font-body text-base sm:text-lg text-primary-foreground/80 text-center max-w-2xl leading-relaxed"
            >
              Sichern Sie sich Ihre Auszeit an der Nordsee in einer unserer komfortablen
              Ferienwohnungen in Cuxhaven-Duhnen. Ob Urlaub zu zweit oder mit der Familie –
              bei uns finden Sie die passende Unterkunft in bester Lage nahe Strand und Promenade.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mt-4 font-body text-sm sm:text-base text-primary-foreground/60 text-center max-w-xl"
            >
              Persönliche Beratung und schnelle Verfügbarkeit – wir helfen Ihnen bei der
              Auswahl Ihrer idealen Ferienwohnung in Cuxhaven.
            </motion.p>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-6"
            >
            {[
              { Icon: Award, text: "Über 35 Jahre Erfahrung" },
              { Icon: MapPin, text: "Direkt in Cuxhaven-Duhnen" },
              { Icon: Waves, text: "Nur wenige Meter zum Strand" },
            ].map((item) => (
              <span
                key={item.text}
                className="flex items-center gap-2 text-sm text-primary-foreground/70 font-body"
              >
                <item.Icon className="w-4 h-4" />
                {item.text}
              </span>
            ))}
            </motion.div>

            {/* CTA Buttons — icon in separate container left of button, nav-style font */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
            >
              {/* Anrufen */}
              <a
                href="tel:0472147760"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium font-body text-primary-foreground border border-primary-foreground/25 hover:text-accent hover:border-accent transition-colors duration-200"
              >
                <Phone className="w-4 h-4" />
                Jetzt anrufen
              </a>

              {/* Anfrage senden */}
              <a
                href="/kontakt"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium font-body text-accent-foreground bg-accent hover:bg-accent/90 transition-colors duration-200"
              >
                <Mail className="w-4 h-4" />
                Anfrage senden
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
